import React from "react"
import { makeStyles } from "@material-ui/styles"
import { Accordion, AccordionSummary, AccordionDetails, Typography, Box, Tooltip, IconButton } from "@material-ui/core"
import { SortableHandle } from "react-sortable-hoc"
import { DragIndicator as DragIndicatorIcon, ExpandMore as ExpandMoreIcon } from "@material-ui/icons"
import { preventDefault } from "../../utils"
import { ColumnBox, RowBox } from "../Boxes"

const useStyles = makeStyles((theme) => ({
  root: {
    overflow: "hidden",
    "& .MuiAccordionSummary-content.Mui-expanded": {
      margin: theme.spacing(1.5, 0)
    },
    borderRadius: theme.radius(1),
    "&.MuiAccordion-rounded:first-child": {
      borderTopLeftRadius: 8,
      borderTopRightRadius: 8
    },
    "&.MuiAccordion-rounded:last-child": {
      borderBottomLeftRadius: 8,
      borderBottomRightRadius: 8
    }
  },
  dragIcon: {
    color: theme.palette.text.secondary,
    paddingLeft: 0,
    marginLeft: -7,
    marginRight: theme.spacing(1)
  },
  title: {
    fontSize: 16,
    fontWeight: "600",
    lineHeight: "20px",
    margin: 0,
    padding: 0,
    wordBreak: "break-word"
  },
  description: {
    fontSize: 14,
    lineHeight: "20px",
    margin: 0,
    padding: 0
  },
  unset: {
    color: theme.palette.text.secondary
  }
}))

const DragHandleActive = SortableHandle(({ className }) => <DragIndicatorIcon className={className} />)

const DragHandle = ({ expanded, className }) =>
  expanded ? <DragIndicatorIcon className={className} /> : <DragHandleActive className={className} />

const ExpansionPanelTitle = ({ icon, classes, title, placeholders, description }) => {
  const internalClasses = useStyles()
  return (
    <>
      {icon && (
        <Box className={classes && classes.icon} display="flex" mr={0.5}>
          {icon}
        </Box>
      )}
      <Box display="flex" alignItems="center" className={classes && classes.summary}>
        <Box flexDirection="column">
          {(title || (placeholders && placeholders.title)) && (
            <Typography variant="h1" className={`${internalClasses.title} ${!title && internalClasses.unset}`}>
              {title || (placeholders && placeholders.title)}
            </Typography>
          )}
          {(description || (placeholders && placeholders.description)) && (
            <Typography
              variant="subtitle1"
              className={`${internalClasses.description} ${!description && internalClasses.unset}`}
            >
              {description || (placeholders && placeholders.description)}
            </Typography>
          )}
        </Box>
      </Box>
    </>
  )
}

const ExpansionPanel = ({
  icon,
  title,
  description,
  children,
  chips,
  sortable,
  sortableTitle = "",
  placeholders,
  expansionClasses,
  expansionSummaryClasses,
  expansionDetailsClasses,
  classes,
  expanded,
  elevation = 0,
  showIcon = true,
  additionalSummary,
  ...rest
}) => {
  const internalClasses = useStyles()

  return (
    <Accordion
      elevation={elevation}
      expanded={expanded}
      classes={{
        root: internalClasses.root,
        ...expansionClasses
      }}
      {...rest}
    >
      <AccordionSummary
        expandIcon={
          showIcon ? (
            <IconButton size="small">
              <ExpandMoreIcon />
            </IconButton>
          ) : null
        }
        classes={expansionSummaryClasses}
      >
        <ColumnBox flexGrow={1}>
          <RowBox flexGrow={1} alignItems="center" width="100%">
            {sortable && (
              <Tooltip title={sortableTitle || "Sort order"}>
                <Box onClick={expanded ? null : preventDefault}>
                  <DragHandle expanded={expanded} className={internalClasses.dragIcon} />
                </Box>
              </Tooltip>
            )}
            <ExpansionPanelTitle
              icon={icon}
              title={title}
              classes={classes}
              placeholders={placeholders}
              description={description}
            />
            <RowBox ml="auto">{chips}</RowBox>
          </RowBox>

          {additionalSummary && <Box className={classes && classes.additionalSummary}>{additionalSummary}</Box>}
        </ColumnBox>
      </AccordionSummary>
      <AccordionDetails className={classes && classes.details} classes={expansionDetailsClasses}>
        {children}
      </AccordionDetails>
    </Accordion>
  )
}

export { ExpansionPanel, ExpansionPanelTitle }
